import { useEffect, useRef, useState } from "react";
import { initializeAnalytics, trackEvent } from "@/lib/analytics/googleAnalytics";
import { useTokenExpirationHandler } from "@/lib/utils/tokenExpirationHandler";
import {
  createRootRoute,
  HeadContent,
  Outlet,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import { SplashScreen } from "@/components/SplashScreen";
// Chatbot temporarily disabled
// import ChatbotWidget from "@/components/ChatbotWidget";

// const CHATBOT_ENABLED = true;

// const CHATBOT_HIDDEN_PATHS = new Set([
//   "/welcome",
//   "/introduction",
//   "/audience",
//   "/path",
//   "/app",
//   "/auth/login",
//   "/auth/register",
// ]);

// const CHATBOT_HIDDEN_PREFIXES = ["/trainer/auth"];

export const Route = createRootRoute({
  component: Root,
});

function Root() {
  // Setup global token expiration handling
  useTokenExpirationHandler();
  const location = useLocation();
  const isLandingPage = location.pathname === "/";
  // const shouldShowChatbot =
  //   CHATBOT_ENABLED &&
  //   !CHATBOT_HIDDEN_PATHS.has(location.pathname) &&
  //   !CHATBOT_HIDDEN_PREFIXES.some((prefix) =>
  //     location.pathname.startsWith(prefix)
  //   );
  const [showSplash, setShowSplash] = useState(true);
  const [fadeSplash, setFadeSplash] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadeSplash(true), 1100);
    const hideTimer = setTimeout(() => setShowSplash(false), 1500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div className="bg-background min-h-screen w-full relative">
      <HeadContent />
      {showSplash && <SplashScreen fadingOut={fadeSplash} />}
      <AnalyticsBridge />
      {/* {shouldShowChatbot && <ChatbotWidget />} */}

      {isLandingPage ? (
        <Outlet />
      ) : (
        <div className="mx-auto w-full max-w-xl">
          <Outlet />
        </div>
      )}
    </div>
  );
}

function AnalyticsBridge() {
  const location = useRouterState({
    select: (state) => state.location,
  });
  const hasTrackedAppAccess = useRef(false);
  const lastTrackedExercise = useRef<string | null>(null);

  useEffect(() => {
    initializeAnalytics();
  }, []);

  useEffect(() => {
    if (!location) return;

    if (!hasTrackedAppAccess.current) {
      trackEvent("app_access");
      hasTrackedAppAccess.current = true;
    }

    const exerciseLabel = getExerciseLabel(location.pathname);
    if (exerciseLabel) {
      if (lastTrackedExercise.current !== exerciseLabel) {
        trackEvent("exercise_viewed", {
          label: exerciseLabel,
        });
        lastTrackedExercise.current = exerciseLabel;
      }
    } else {
      lastTrackedExercise.current = null;
    }
  }, [location?.pathname, location?.search, location?.hash]);

  return null;
}

function getExerciseLabel(pathname: string): string | null {
  if (!pathname) return null;
  const segments = pathname.split("/").filter(Boolean);
  const exerciseIndex = segments.indexOf("exercises");
  if (exerciseIndex === -1) return null;

  const relevantSegments = segments.slice(exerciseIndex + 1);
  if (relevantSegments.length < 2) return null;

  return relevantSegments.join("/");
}
